import { Column, Entity, Index, JoinColumn, ManyToOne } from 'typeorm';
import { AutomationBatchEntity } from './automation-batch.entity';
import { AutomationTargetListEntity } from './automation-target-list.entity';
import { BaseEntity } from './base.entity';
import { GroupEntity } from './group.entity';
import { ListSavedSearchEntity } from './list-saved-search.entity';

export type AutomationBatchTargetListSourceType =
	| 'static-list'
	| 'group'
	| 'saved-list';

/**
 * Links a batch to a source for execution.
 * Sources can be static target lists, groups, or saved lists (saved searches).
 *
 * Sources are processed in order: includes first (sorted by id), then excludes (sorted by id).
 * Variable mapping transforms source entity fields to automation input variables.
 */
@Entity('_automation_batch_target_list')
export class AutomationBatchTargetListEntity extends BaseEntity {
	@Column({
		name: '_fk__automation_batch__id',
		type: 'int',
		unsigned: true,
	})
	public batchId!: number;

	@Column({
		name: '_execution_mode',
		type: 'enum',
		enum: ['include', 'exclude'],
		default: "'include'",
	})
	public executionMode!: 'include' | 'exclude';

	@Column({
		name: '_variable_mapping',
		type: 'json',
		nullable: true,
		comment: 'Maps source entity fields to automation input variables',
	})
	public variableMapping?: Record<string, string>;

	// === Source Type ===

	@Column({
		name: '_source_type',
		type: 'enum',
		enum: ['static-list', 'group', 'saved-list'],
		default: "'static-list'",
	})
	public sourceType!: AutomationBatchTargetListSourceType;

	/**
	 * Entity type we're extracting from the source.
	 * - For groups: required ('client', 'item', 'user') since groups can have multiple entity types
	 * - For saved-list: derived from listKey (e.g., 'client' from listKey='client')
	 * - For static-list: optional (the list items contain the data directly)
	 */
	@Column({
		name: '_entity_type',
		type: 'varchar',
		length: 50,
		nullable: true,
	})
	public entityType?: string;

	// === Source References (only one populated based on sourceType) ===

	@Column({
		name: '_fk__automation_target_list__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public targetListId: number | null = null;

	@Column({
		name: '_fk__group__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public groupId: number | null = null;

	@Column({
		name: '_fk__list_saved_search__id',
		type: 'int',
		unsigned: true,
		nullable: true,
	})
	public savedListId: number | null = null;

	// === Relations ===

	@ManyToOne(() => AutomationBatchEntity)
	@JoinColumn({ name: '_fk__automation_batch__id' })
	public batch!: AutomationBatchEntity;

	@ManyToOne(() => AutomationTargetListEntity, (list) => list.batchLists, {
		nullable: true,
	})
	@JoinColumn({ name: '_fk__automation_target_list__id' })
	public targetList?: AutomationTargetListEntity;

	@ManyToOne(() => GroupEntity, { nullable: true })
	@JoinColumn({ name: '_fk__group__id' })
	public group?: GroupEntity;

	@ManyToOne(() => ListSavedSearchEntity, { nullable: true })
	@JoinColumn({ name: '_fk__list_saved_search__id' })
	public savedList?: ListSavedSearchEntity;

	// === Indexes ===

	@Index('idx_automation_batch_target_list_batch')
	public static batchIndex: [string] = ['batchId'];

	@Index('idx_automation_batch_target_list_list')
	public static listIndex: [string] = ['targetListId'];

	@Index('idx_automation_batch_target_list_mode')
	public static modeIndex: [string] = ['executionMode'];

	@Index('idx_automation_batch_target_list_source_type')
	public static sourceTypeIndex: [string] = ['sourceType'];

	@Index('idx_automation_batch_target_list_group')
	public static groupIndex: [string] = ['groupId'];

	@Index('idx_automation_batch_target_list_saved_list')
	public static savedListIndex: [string] = ['savedListId'];
}
